import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";
import { isValidPhoneNumber } from "react-phone-number-input";
import * as yup from "yup";

import { onBoardingContext } from "../context/onBoardingContext";
import { Company } from "../models/company";
import { checkValidUrl } from "../util/checkValidUrl";

export default function useOnBoardingContext(isOnBoardingPage?: boolean) {
  const { t: translate } = useTranslation();
  const onBoarding = useContext(onBoardingContext);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const schema = yup.object().shape({
    name: yup.string().required(translate("thisFieldIsRequired")),
    webSite: isOnBoardingPage
      ? yup.string().nullable(true)
      : yup.string().nullable(true).test(
        "check-url",
        translate("thisFieldIsAnInvalidUrl"),
        (webSite) => (webSite ? checkValidUrl(webSite) : true)
      ),
    phone: yup.string()
      .required(translate("thisFieldIsRequired"))
      .test({
        name: "validate-company-phone",
        message: translate("pleaseEnterAValidPhoneNumber"),
        test: (value) => isValidPhoneNumber(value || ""),
      }),
    email: yup.string().email().required(translate("thisFieldIsRequired")),
    registrationNumber: yup.string()
      .required(translate("thisFieldIsRequired"))
      .matches(/^\s*\d*([0-9]\d*\s*)*$/, translate("thisFieldShouldHaveDigitsOnly")),
    location: isOnBoardingPage
      ? yup.object().test(
          "location-required",
          translate("thisFieldIsRequired"),
          function (location: any) {
            if (!location) return false;
            const loc = location as Record<string, any>;
            const hasAddressLine1 = loc.addressLine1?.trim?.();
            const hasSuburb = loc.suburb?.trim?.();
            const hasCountry = loc.country?.trim?.();
            return !!(hasAddressLine1 && hasSuburb && hasCountry);
          }
        )
      : yup.object().nullable(true),
  });

  const handleValidationSuccess = (company: Company | undefined): boolean => {
    schema.validateSync(
      {
        name: company?.name,
        webSite: company?.webSite,
        phone: company?.phone,
        email: company?.email,
        registrationNumber: company?.registrationNumber,
        location: company?.location,
      },
      { abortEarly: false }
    );
    setErrors([]);

    return true;
  };

  const handleValidationError = (error: yup.ValidationError): boolean => {
    if (error?.inner) setErrors(error?.inner);

    return false;
  };

  const handleValidate = (company?: Company) => {
    try {
      return handleValidationSuccess(company);
    } catch (error) {
      if (error instanceof yup.ValidationError) return handleValidationError(error);

      return false;
    }
  };

  return {
    ...onBoarding,
    handleValidate,
    errors,
    setErrors,
  };
}
